import { PropsWithChildren, Children, cloneElement, ReactElement } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";

import { PricingCardProps } from "@/libraries/design/components/pricing-card/src/pricing-card";
import { wacToken } from "@/libraries/design/foundations";

export interface PricingCardsProps extends PropsWithChildren {
    selectedCardIndex?: number;
    numSections?: number;
    hasCalloutSlot?: boolean;
    hasRangeSlot?: boolean;
    hasLinkSlot?: boolean;
    hasBannerSlot?: boolean;
}

// considerations while building this component:
// in my opinion, this component is way too complex and should be refactored
// it tries to preserve existing patterns in using CSS grid, where the consumer needs to
// be concerned about the layout of the grid children, which is difficult to reason about and if
// the HTML structure or children changes, that would break the layout; this is to address the
// problem of aligning the middle divider. a strong design requirement is to align the plan titles,
// prices, button, link text, and divider -- if implementing this again, a possible architecture to
// consider is introducing "slots" that take up a designated space -- the pricing calculator would
// need to know about these "slots" but it would perhaps make things easier to reason about
// other challenges include: compiled CSS requiring static styles, but only support
// nested selectors, whereas inline styles supports dynamic styles but don't support nested selectors
// and therefore tracking down what is setting a particularly difficult -- it could be:
// 1. the pricing card component itself
// 2. the media query styling
// 3. inline styles as props
export const PricingCards = (props: PricingCardsProps) => {
    const {
        selectedCardIndex,
        children,
        numSections = 2,
        hasCalloutSlot,
        hasRangeSlot,
        hasLinkSlot,
        hasBannerSlot
    } = props;

    const numberOfChildren = Children.count(children);

    const getBorderProps = (childIndex: number, numSections: number) => {
        if (numSections !== 1) {
            return {};
        }

        const isFirstChild = childIndex === 1;
        const isLastChild = childIndex === numberOfChildren;

        return {
            topSectionStyle: {
                borderStartEndRadius: isFirstChild ? "0" : undefined,
                borderEndEndRadius: isFirstChild ? "0px" : undefined,
                borderStartStartRadius: isLastChild ? "0" : undefined,
                borderEndStartRadius: isLastChild ? "0px" : undefined
            }
        };
    };

    const getSelectedBorderProps = (childIndex: number) => {
        if (
            selectedCardIndex === undefined ||
            selectedCardIndex !== childIndex
        ) {
            return {};
        }

        const isFirstChild = childIndex === 1;
        const isLastChild = childIndex === numberOfChildren;

        const blackBorder = `1px solid ${wacToken("color.primary.black")}`;

        return {
            topSectionStyle: {
                borderInlineEnd: blackBorder,
                borderInlineStart: blackBorder,
                borderBlockStart: blackBorder,
                borderLeftRadius: "10px",
                borderTopRightRadius:
                    numSections === 1 && isLastChild ? "20px" : undefined,
                borderBottomLeftRadius:
                    numSections === 1 && isLastChild ? "0px" : undefined,
                borderBottomRightRadius:
                    numSections === 1 && isFirstChild ? "0px" : undefined,
                borderRadius:
                    numSections === 1 && !isFirstChild && !isLastChild
                        ? `0px`
                        : undefined,
                borderBottom: numSections === 1 ? blackBorder : undefined
            },

            bottomSectionStyle: {
                borderInlineEnd: blackBorder,
                borderInlineStart: blackBorder,
                borderBlockEnd: blackBorder
            }
        };
    };

    return (
        <div
            css={css({
                gridTemplateRows: `repeat(${numSections}, auto)`,
                gridTemplateColumns: `repeat(${numberOfChildren}, minmax(0, 384px))`,
                display: "grid",
                gridAutoFlow: "column",
                width: "fit-content",
                padding: "12px",

                [media.above.md]: {
                    "width": "fit-content",
                    // first direct child border
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    "& > *": {
                        borderInlineStart: `1px solid ${wacToken(
                            "color.neutral.light400"
                        )}`,
                        borderBlockStart:
                            numSections === 1
                                ? `1px solid ${wacToken(
                                      "color.neutral.light400"
                                  )}`
                                : undefined,
                        borderBlockEnd:
                            numSections === 1
                                ? `1px solid ${wacToken(
                                      "color.neutral.light400"
                                  )}`
                                : undefined
                    },
                    // every odd child 1, 3, 5, 7 border top
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    "& > *:nth-child(odd)": {
                        borderBlockStart: `1px solid ${wacToken(
                            "color.neutral.light400"
                        )}`
                    },
                    // every even child 2, 4, 6, 8 border bottom
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    "& > *:nth-child(even)": {
                        borderBlockEnd: `1px solid ${wacToken(
                            "color.neutral.light400"
                        )}`
                    },
                    // first child border radius top left
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    "& > *:first-child": {
                        borderStartStartRadius: "20px"
                    },
                    // second child border radius bottom left
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    "& > *:nth-child(2)": {
                        borderEndStartRadius:
                            numSections === 1 ? "0px" : "20px",
                        borderEndEndRadius:
                            numSections === 1 ? "0px" : undefined
                    },
                    // last two children border top right and bottom right
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    "& > *:nth-last-child(2)": {
                        borderStartEndRadius:
                            numSections === 1 ? "0px" : "20px",
                        borderInlineEnd: `1px solid ${wacToken(
                            "color.neutral.light400"
                        )}`
                    },
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    "& > *:last-child": {
                        borderEndEndRadius: "20px",
                        borderStartEndRadius:
                            numSections === 1 ? "20px" : undefined,
                        borderInlineEnd: `1px solid ${wacToken(
                            "color.neutral.light400"
                        )}`
                    }
                },
                [media.below.md]: {
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    "& > *:nth-child(even)": {
                        borderEndStartRadius: "20px",
                        borderEndEndRadius: "20px",
                        borderBlockEnd: `1px solid ${wacToken(
                            "color.neutral.light400"
                        )}`,
                        borderInlineStart: `1px solid ${wacToken(
                            "color.neutral.light400"
                        )}`,
                        borderInlineEnd: `1px solid ${wacToken(
                            "color.neutral.light400"
                        )}`,
                        ...(numSections !== 1 && {
                            // padding for each card with multiple sections
                            marginBlockEnd: "8px"
                        })
                    },
                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    "& > *:nth-child(odd)": {
                        borderStartStartRadius: "20px",
                        borderStartEndRadius: "20px",
                        borderBlockStart: `1px solid ${wacToken(
                            "color.neutral.light400"
                        )}`,
                        borderInlineStart: `1px solid ${wacToken(
                            "color.neutral.light400"
                        )}`,
                        borderInlineEnd: `1px solid ${wacToken(
                            "color.neutral.light400"
                        )}`
                    },
                    "display": "flex",
                    "flexDirection": "column",
                    "width": "100%",
                    ...(numSections === 1 && {
                        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                        "& > *": {
                            // this important flag is important to override the getBorderProps style props
                            // on a mobile viewport
                            borderRadius: "20px !important",
                            // padding for each card with no subsection
                            marginBlockEnd: "8px",
                            borderBlockStart: `1px solid ${wacToken(
                                "color.neutral.light400"
                            )}`,
                            borderBlockEnd: `1px solid ${wacToken(
                                "color.neutral.light400"
                            )}`
                        }
                    })
                }
            })}
        >
            {Children.map(children, (child, index) => {
                if (!child) return null;

                return cloneElement(child as ReactElement<PricingCardProps>, {
                    key: index,
                    hasCalloutSlot,
                    hasRangeSlot,
                    hasLinkSlot,
                    hasBannerSlot,
                    ...(index + 1 === selectedCardIndex
                        ? getSelectedBorderProps(index + 1)
                        : getBorderProps(index + 1, numSections))
                });
            })}
        </div>
    );
};
